import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useMetricDropdowns } from "@hooks/useDropdownInitialization";
import { metricsSelectDropdown } from "@components/Common/utils";
import { useDataAccess } from "@components/PermissionedComponents";
import { LockedGroupLabel, NO_PERMISSION_LABEL } from "@components/PermissionedComponents/lockedStreamGroups";

/** Metric categories, in display order. */
const METRIC_CATEGORIES = [
  { key: "gps", label: "GPS" },
  { key: "testing", label: "Testing" },
  { key: "forceplate", label: "Forceplate" },
  { key: "wellness", label: "Wellness" },
  { key: "nutrition", label: "Nutrition" },
  { key: "cognition", label: "Cognition" },
];

/** Counts sit apart from the measured metrics, and last: a coach scans the GPS list
 *  first, and a count answers a different question. The backend serves them only once
 *  its own gate opens, so an empty list simply means the group is absent. */
const COUNT_CATEGORY = { key: "count", label: "Counts" };

/** Helmet impact metrics (Riddell InSite). The team reducer keeps them apart from
 *  `all_metric_types`, and the dropdown tree carries the aggregations each one allows. */
const HELMET_CATEGORY = { key: "helmet", label: "Helmet" };

/**
 * Shared metric-dropdown source. Loads the metric dropdowns from Redux and builds
 * the grouped `<Select>` options (with prettified labels from `metricsSelectDropdown`).
 *
 * Previously this exact block was re-implemented in MetricDropdownStep, the Radar
 * and Table2D configurators, and (in a label-less variant) TableColumnForm.
 *
 * Returns:
 *  - `metricDropdown` — grouped options for an antd `<Select>` (see MetricSelect).
 *  - `all_metric_types` — the raw Redux metric catalogue (for default-selection logic).
 *  - `labelMaps` — id → display-label maps by category (for resolving names elsewhere).
 */
export interface ReduxMetricDropdownOptions {
  /** Add the Counts group. Off by default: the backend accepts a count only on
   *  chart, table, table_2d, gauge, pie_chart and stat_tile, so a caller that builds
   *  any other widget — radar above all — must not offer one. */
  includeCounts?: boolean;
  /** Add the Helmet group. Off by default: the backend serves a helmet metric on the
   *  stat tile, chart, gauge, radar and scatterplot. The table, table_2d and pie
   *  configurators keep it off until the backend confirms them. */
  includeHelmet?: boolean;
}

export function useReduxMetricDropdown({
  includeCounts = false,
  includeHelmet = false,
}: ReduxMetricDropdownOptions = {}) {
  const {
    dropdowns: {
      forceplate: forceplateDropdowns,
      testing: testingDropdowns,
      gps: gpsDropdowns,
      helmet: helmetDropdowns,
    },
    all_metric_types,
    helmet_metrics,
  } = useSelector(({ team }: any) => team);

  useMetricDropdowns(["forceplate", "testing", "gps", "helmet"]);

  // A widget must not be buildable on a stream the user cannot read.
  const { canAccess } = useDataAccess();

  const labelMaps = useMemo(() => {
    const { gpsLabelMap, testingLabelMap, forceplateLabelMap } = metricsSelectDropdown(
      all_metric_types, gpsDropdowns, forceplateDropdowns, testingDropdowns,
    );
    return { gps: gpsLabelMap, testing: testingLabelMap, forceplate: forceplateLabelMap };
  }, [all_metric_types, gpsDropdowns, forceplateDropdowns, testingDropdowns]);

  const metricDropdown = useMemo(() => {
    const groups: any[] = [];
    METRIC_CATEGORIES.forEach(({ key, label }) => {
      const metrics: any[] = all_metric_types?.[key] ?? [];
      // No data for this team: the group is absent, same as before.
      if (metrics.length === 0) return;
      // Data exists but is not readable: keep the heading so the user can see the
      // stream is there, with a single unclickable row instead of the metric list.
      if (!canAccess(key as any)) {
        groups.push({
          label,
          displayLabel: <LockedGroupLabel label={label} />,
          key,
          options: [
            {
              label: NO_PERMISSION_LABEL,
              displayLabel: NO_PERMISSION_LABEL,
              value: `${key}__no-permission`,
              key: `${key}__no-permission`,
              disabled: true,
            },
          ],
        });
        return;
      }
      const map: Map<string, string> | undefined = (labelMaps as any)[key];
      const options = metrics.map((m: any) => ({
        label: m.name,
        displayLabel: map?.get(m.unique_identifier) ?? m.name,
        value: m.unique_identifier,
        key: m.unique_identifier,
      }));
      if (key !== "gps") {
        options.sort((a: any, b: any) =>
          a.displayLabel.toLowerCase().localeCompare(b.displayLabel.toLowerCase()),
        );
      }
      groups.push({ label, displayLabel: label, key, options });
    });

    // The backend returns an empty helmet tree for a team without a helmet integration.
    // The locked heading reads the metric list, which the backend serves to every role.
    const helmetMetrics: any[] = helmet_metrics ?? [];
    if (includeHelmet && helmetMetrics.length > 0) {
      if (!canAccess("helmet")) {
        groups.push({
          label: HELMET_CATEGORY.label,
          displayLabel: <LockedGroupLabel label={HELMET_CATEGORY.label} />,
          key: HELMET_CATEGORY.key,
          options: [
            {
              label: NO_PERMISSION_LABEL,
              displayLabel: NO_PERMISSION_LABEL,
              value: `${HELMET_CATEGORY.key}__no-permission`,
              key: `${HELMET_CATEGORY.key}__no-permission`,
              disabled: true,
            },
          ],
        });
      } else if ((helmetDropdowns ?? []).length > 0) {
        groups.push({
          label: HELMET_CATEGORY.label,
          displayLabel: HELMET_CATEGORY.label,
          key: HELMET_CATEGORY.key,
          options: (helmetDropdowns as any[]).map((node: any) => ({
            label: node.label,
            displayLabel: node.label,
            value: node.key,
            key: node.key,
            aggregations: node.aggregations,
          })),
        });
      }
    }

    // Counts carry no colour strategy and no per-metric label map, so they need none
    // of the treatment above — just their names, after everything measured.
    const counts: any[] = all_metric_types?.[COUNT_CATEGORY.key] ?? [];
    // The backend is the gate: it returns count entries only for a team the rollout
    // has reached, so an empty list means this team has no counts.
    if (includeCounts && counts.length > 0) {
      groups.push({
        label: COUNT_CATEGORY.label,
        displayLabel: COUNT_CATEGORY.label,
        key: COUNT_CATEGORY.key,
        options: counts.map((m: any) => ({
          label: m.name,
          displayLabel: m.name,
          value: m.unique_identifier,
          key: m.unique_identifier,
        })),
      });
    }

    return groups;
  }, [all_metric_types, helmet_metrics, helmetDropdowns, labelMaps, canAccess, includeCounts, includeHelmet]);

  return { metricDropdown, all_metric_types, labelMaps };
}
